<template>
  <lf-modal :title="modalTitle" :close="() => emit('close')">
    <template #content>
      <loader :is-loading="loading" />
      <form v-if="!!agent" @submit="handleSave">
        <div class="flex-col space-y-4 p-6">
          <lf-input
            :value="agent.name"
            name="name"
            :placeholder="$t('AI_AGENTS.EDIT_MODAL.NAME')"
            data-cy="ai-agent-edit-name"
            @key-released="agent.name = $event"
          />
          <lf-input
            :value="agent.description"
            name="description"
            :placeholder="$t('AI_AGENTS.EDIT_MODAL.DESCRIPTION')"
            data-cy="ai-agent-edit-description"
            @key-released="agent.description = $event"
          />
          <template v-if="!!agent.content">
            <lf-dropdown
              :value="agent.content.agent.type"
              :options="typeOptions"
              :placeholder="$t('COMMON.TYPE')"
              full-width
              @change="handleAgentTypeChange"
            />
            <lf-input
              v-if="agent.content.agent.type === 'webhook'"
              :value="agent.content?.agent.webhook_url"
              name="webhook_url"
              :placeholder="$t('AI_AGENTS.WEBHOOK')"
              data-cy="ai-agent-edit-webhook_url"
              @key-released="agent.content.agent.webhook_url = $event"
            />
          </template>
        </div>
        <div class="flex justify-end border-t border-gray-200 p-6">
          <modal-buttons
            class="bg-white"
            save-is-submit
            no-vertical-padding
            :on-cancel="() => emit('close')"
            :disabled="isSubmitting"
          />
        </div>
      </form>
      <div v-if="getActiveAgentError" class="py-12 text-center">
        {{ $t("COMMON.GENERIC_ERROR") }}
      </div>
    </template>
  </lf-modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { NEW_AGENT } from "@/helpers/constants/aiAgents";
import { formatResponseErrors } from "@/helpers/vee-validate";
import { usePromiseWrapper } from "@/hooks/common";
import { aiAgentsApiService } from "@/services/modules/aiAgents";
import { useAiAgentsStore } from "@/stores/ai/agents";

import Loader from "@/components/ui/Loader.vue";
import LfModal from "@/components/ui/Modal.vue";
import ModalButtons from "@/components/ui/ModalButtons.vue";
import LfDropdown from "@/components/ui/inputs/LfDropdown.vue";

import type {
  AiAgent,
  CreateAiAgentPayload,
  UpdateAiAgentPayload,
} from "@/models/aiAgents";
import type { HTTPError } from "@/models/common";

const { agentId } = defineProps<{
  agentId?: AiAgent["id"];
}>();

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const { handleSubmit, isSubmitting, setErrors } = useForm();
const aiAgentsStore = useAiAgentsStore();
const { options } = storeToRefs(aiAgentsStore);

const agent = ref<Partial<AiAgent> | null>(null);

const mode = computed<"create" | "update">(() =>
  agentId ? "update" : "create"
);

const modalTitle = computed(() =>
  mode.value === "create"
    ? t("AI_AGENTS.EDIT_MODAL.TITLE_CREATE")
    : t("AI_AGENTS.EDIT_MODAL.TITLE_EDIT")
);

const typeOptions = computed(() => options.value?.definition.agent_types ?? {});

const handleSave = handleSubmit(async () => {
  if (!agent.value) {
    return;
  }

  try {
    if (mode.value === "create") {
      await aiAgentsStore.create(agent.value as CreateAiAgentPayload);
    }

    if (mode.value === "update" && agent.value.id) {
      await aiAgentsStore.update(
        agent.value.id,
        agent.value as UpdateAiAgentPayload
      );
    }

    emit("close");
  } catch (error) {
    const formattedErrors = formatResponseErrors(
      (error as HTTPError).response.data.errors
    );
    setErrors(formattedErrors);
  }
});

const {
  fetchWrapper: getActiveAgent,
  loading,
  error: getActiveAgentError,
} = usePromiseWrapper(async () => {
  if (!!agentId && mode.value === "update") {
    return await aiAgentsApiService.getById(agentId);
  }

  return structuredClone(NEW_AGENT);
});

const handleAgentTypeChange = (type: string | number) => {
  if (!agent.value?.content) {
    return;
  }

  agent.value.type = type as NonNullable<AiAgent["type"]>;
  agent.value.content.agent.type = type as NonNullable<AiAgent["type"]>;
};

watch(
  () => agentId,
  async () => {
    agent.value = await getActiveAgent();
  },
  { immediate: true }
);
</script>
